import type { CodeSessionRow, CreateCodeSessionTarget } from '../types';
import type { SessionCapabilities, SessionCapabilityFields } from './types';

import { isLocalSessionId } from './session-id';

import {
  LOCAL_SESSION_IMAGE_BASE64_BYTES,
  localImageBase64Bytes,
} from '@/lib/code-sessions/image-limits';

/**
 * What a session that only talks to its agent lets the client do: send it messages, and nothing
 * else, as before a finding's conversation has its first message.
 */
export const MESSAGE_ONLY_CAPABILITIES: SessionCapabilities = Object.freeze({
  send: true,
  sideAgents: false,
  gitActions: false,
  pullRequest: false,
  createPullRequest: false,
  attachments: false,
  imagesOnly: null,
  manage: false,
  previews: false,
  subagents: false,
  skills: false,
  queuedEdits: false,
  modelCosts: false,
  workspace: false,
  github: false,
  environment: false,
  readMarks: false,
  aiSubscriptions: false,
  reasoningEffort: false,
  approvalModes: false,
});

/**
 * A session on the person's own computer only talks to its agent. Everything
 * else calls cubic's cloud API, which keeps no record of the session.
 */
const LOCAL_SESSION_CAPABILITIES: SessionCapabilities = Object.freeze({
  ...MESSAGE_ONLY_CAPABILITIES,
  attachments: true,
  imagesOnly: { room: LOCAL_SESSION_IMAGE_BASE64_BYTES, cost: localImageBase64Bytes },
  skills: true,
  approvalModes: true,
});

/**
 * What a cloud session's row lets the client do, by the server's own rules.
 * Only cubic's runner sends to a session cubic owns. The server refuses side
 * agents started from a side agent or a setup-only session. A side agent
 * leaves its branch to its main session, and a setup-only session's server
 * also refuses Git actions, pull requests and files (`isSetupOnlySession`). A
 * row without `sideAgentParentId` or `setupOnly`, as a list's summaries and
 * the client's placeholders are, is a main session that is not setup-only.
 */
function cloudCapabilities(row: Omit<SessionCapabilityFields, 'id'>): SessionCapabilities {
  const mainSession = !row.sideAgentParentId;
  const setupOnly = row.setupOnly === true;
  return {
    send: row.source !== 'cubic',
    sideAgents: mainSession && !setupOnly,
    gitActions: mainSession && !setupOnly,
    pullRequest: !setupOnly,
    createPullRequest: !setupOnly && mainSession,
    attachments: !setupOnly,
    imagesOnly: null,
    // Each of these controls keeps the checks it makes for a cloud session.
    manage: true,
    previews: true,
    subagents: true,
    skills: true,
    queuedEdits: true,
    modelCosts: true,
    workspace: true,
    github: true,
    environment: true,
    readMarks: true,
    aiSubscriptions: true,
    reasoningEffort: true,
    approvalModes: false,
  };
}

/** What a session's row lets the client do: a `ses_` id is a session on the person's own computer. */
export function capabilitiesOf(row: SessionCapabilityFields): SessionCapabilities {
  return isLocalSessionId(row.id) ? LOCAL_SESSION_CAPABILITIES : cloudCapabilities(row);
}

/**
 * What a session about to start will let the client do, by where it runs, before it has a row:
 * the start composer's own controls, and the placeholder that shows the session while it is
 * created. A cloud one reads as a main session a person starts from the web.
 */
export function capabilitiesOfTarget(kind: CreateCodeSessionTarget['kind']): SessionCapabilities {
  return kind === 'local' ? LOCAL_SESSION_CAPABILITIES : cloudCapabilities({ source: 'web' });
}

/** The session's sandbox takes reads and commands: ready, with no resume replacing it. */
export function isRuntimeReady(snapshot: {
  session: Pick<CodeSessionRow, 'sandboxStatus'>;
  resuming: boolean;
}): boolean {
  return snapshot.session.sandboxStatus === 'ready' && !snapshot.resuming;
}
